import React, { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { useTabs } from '../../../context/TabsContext';
import { boardTasksLive } from './boardTasksLive';
import { sameBoardProps } from './utils/sameBoardProps';
import { useBoardScene } from './hooks/useBoardScene';
import { useBoardCamera } from './hooks/useBoardCamera';
import { useBoardPointer } from './hooks/useBoardPointer';
import { useBoardDelete } from './hooks/useBoardDelete';
import BoardCanvas from './components/BoardCanvas';
import BoardWires from './components/BoardWires';
import BoardChrome from './components/BoardChrome';
import BoardHandles from './components/BoardHandles';
import CanvasContextMenu from '../FlowchartView/components/CanvasContextMenu';
import { OPTIMISTIC_CAMPAIGN_CARD } from '../../../hooks/campaignNesting/optimisticCampaignCard';
import '../FlowchartView/styles/edges.css';
import './BoardView.css';

const EMPTY_SELECTION = new Set();

/**
 * The flow's mission cards and connections, moved as one layer.
 * The flow view itself is unchanged.
 */
function BoardView({
  tasks = [],
  statuses = [],
  onOpenDetails,
  flowProps = null,
  lockStates = {},
  selectedIds = EMPTY_SELECTION,
  onSelectionChange,
  fokus = null,
}) {
  const wrapRef = useRef(null);
  const worldRef = useRef(null);
  const boxRef = useRef(null);
  const cameraRef = useRef({ x: 48, y: 48, zoom: 1 });
  const flyFrameRef = useRef(0);
  const gestureRef = useRef(false);
  const selectedRef = useRef(selectedIds);
  const onSelectionChangeRef = useRef(onSelectionChange);
  const flowRef = useRef(null);
  const menuActionsRef = useRef(null);
  const onPositionChangeBatchRef = useRef(null);
  const tidyFocusRef = useRef(null);
  const tidySettledRef = useRef(null);
  const frameAfterLayoutRef = useRef(false);
  const onEmptyContextMenuRef = useRef(null);
  const canvasMenuRef = useRef(null);
  if (!onEmptyContextMenuRef.current) {
    onEmptyContextMenuRef.current = {
      reveal: (screen, canvas) => canvasMenuRef.current?.reveal(screen, canvas),
      dismiss: () => canvasMenuRef.current?.dismiss(),
    };
  }
  const { activeTabId } = useTabs();
  selectedRef.current = selectedIds;
  onSelectionChangeRef.current = onSelectionChange;
  boardTasksLive.current = tasks;
  useEffect(() => () => {
    boardTasksLive.current = null;
  }, []);

  const flow = useMemo(() => ({
    ...(flowProps || {}),
    tasks,
    statuses,
    onOpenDetails: flowProps?.onOpenDetails || onOpenDetails,
  }), [flowProps, tasks, statuses, onOpenDetails]);
  flowRef.current = flow;
  onPositionChangeBatchRef.current = flow.onPositionChangeBatch;

  const chainOrder = useMemo(() => {
    const chains = flow.chains;
    if (!Array.isArray(chains) || chains.length === 0) return null;
    return [...chains]
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      .map((chain) => chain.id);
  }, [flow.chains]);

  const scene = useBoardScene({
    tasks,
    campaignId: flow.campaignId,
    tabId: activeTabId,
    wrapRef,
    worldRef,
    cameraRef,
    flyFrameRef,
    gestureRef,
    onPositionChangeBatchRef,
    tidyFocusRef,
    tidySettledRef,
    frameAfterLayoutRef,
    fokusActive: !!fokus?.active,
    chainOrder,
  });

  const { goToTask } = useBoardCamera({
    tasks,
    wrapRef,
    worldRef,
    cameraRef,
    flyFrameRef,
    tasksRef: scene.tasksRef,
    positionsRef: scene.positionsRef,
    boxesRef: scene.boxesRef,
    onSelectionChangeRef,
    gestureRef,
  });

  useBoardPointer({
    wrapRef,
    worldRef,
    boxRef,
    cameraRef,
    flyFrameRef,
    gestureRef,
    tasksRef: scene.tasksRef,
    positionsRef: scene.positionsRef,
    boxesRef: scene.boxesRef,
    selectedRef,
    onSelectionChangeRef,
    onEmptyContextMenuRef,
  });

  useBoardDelete({
    tasksRef: scene.tasksRef,
    selectedRef,
    onSelectionChangeRef,
    flowRef,
  });

  const fokusWas = useRef(null);
  useEffect(() => {
    const active = !!fokus?.active;
    if (fokusWas.current === null) {
      fokusWas.current = active;
      return undefined;
    }
    if (fokusWas.current === active) return undefined;
    const entering = active && fokusWas.current === false;
    fokusWas.current = active;
    if (entering) frameAfterLayoutRef.current = true;
    return undefined;
  }, [fokus?.active]);

  useEffect(() => {
    canvasMenuRef.current?.dismiss();
  }, [flow.campaignId]);

  useEffect(() => {
    const onOptimistic = (event) => {
      const taskId = event.detail?.taskId;
      if (!taskId || event.detail?.sprintId !== flow.campaignId) return;
      tidyFocusRef.current = taskId;
    };
    window.addEventListener(OPTIMISTIC_CAMPAIGN_CARD, onOptimistic);
    return () => window.removeEventListener(OPTIMISTIC_CAMPAIGN_CARD, onOptimistic);
  }, [flow.campaignId]);

  const placeNewCard = (nodeType, position) => {
    const taskId = flowRef.current?.onAddTask?.(null, null, nodeType, { position });
    if (!taskId) return;
    tidyFocusRef.current = taskId;
    onSelectionChangeRef.current?.(new Set([taskId]));
  };

  const closeCanvasMenu = useCallback(() => {
    canvasMenuRef.current?.dismiss();
  }, []);

  useEffect(() => {
    if (!fokus?.active) return undefined;
    const onKey = (event) => {
      if (event.key !== 'Escape') return;
      const tag = event.target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || event.target?.isContentEditable) return;
      fokus.onExit?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [fokus?.active, fokus?.onExit]);

  return (
    <div className="board-view" ref={wrapRef}>
      <div className="board-view__dots" aria-hidden />
      <BoardWires edges={scene.edges} tasks={tasks} cameraRef={cameraRef} />
      <BoardCanvas
        worldRef={worldRef}
        boxRef={boxRef}
        tasks={tasks}
        positions={scene.positions}
        nodeDimensions={scene.graph.nodeDimensions}
        selectedIds={selectedIds}
        isFocusMode={!!fokus?.active}
        flow={flow}
        lockStates={lockStates}
        statuses={statuses}
        goToTask={goToTask}
        menuActionsRef={menuActionsRef}
      />
      <div className="board-view__pan-shield" />
      <BoardHandles
        tasks={tasks}
        graph={scene.graph}
        edges={scene.edges}
        selectedIds={selectedIds}
        wrapRef={wrapRef}
        worldRef={worldRef}
        cameraRef={cameraRef}
        flowRef={flowRef}
        gestureRef={gestureRef}
        onSelectionChangeRef={onSelectionChangeRef}
        menuActionsRef={menuActionsRef}
        tidyFocusRef={tidyFocusRef}
        tidySettledRef={tidySettledRef}
      />
      <BoardChrome
        flow={flow}
        tasks={tasks}
        statuses={statuses}
        lockStates={lockStates}
        goToTask={goToTask}
        graph={scene.graph}
        edges={scene.edges}
        cameraRef={cameraRef}
        wrapRef={wrapRef}
        fokus={fokus}
      />
      <CanvasContextMenu
        ref={canvasMenuRef}
        onAddMission={(position) => placeNewCard('task', position)}
        onAddDecision={(position) => placeNewCard('decision', position)}
        onAddWait={(position) => placeNewCard('wait', position)}
        onCreateCampaign={flow.onCreateCampaign}
        onPaste={(position) => menuActionsRef.current?.pasteAtPosition?.(position)}
        onClose={closeCanvasMenu}
      />
    </div>
  );
}

export default memo(BoardView, sameBoardProps);
